import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import Container from '../../components/Container';
import NumberInput from '../../components/NumberInput';
import { Modal, Button, Form } from 'react-bootstrap';
import OfferTable from './OfferTable';
import buildGazCommercialProposalBlocks, { buildGazCommercialProposalSendPayload } from './buildGazCommercialProposalBlocks';
import { resolveGazRowManualPrice } from './gazClientProposalText';
import { buildGazMessageIntro, buildGazMessageTotalLines } from './gazProposalMessage';
import { buildVtoroySortMessageLines } from './gazVtoroySort';
import './gazProposal.css';
import ClientInfo from './ClientInfo';
import Tooltip from './Tooltip';
import { useOfferState } from './useOfferState';
import { useOptions } from '../../OptionsContext';
import { useAnalogsData } from './AnalogsDataContext';
import DropdownTable from '../../components/DropdownTable';
import LogistRequestModal from '../Productions/LogistRequestModal';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getWazzupChannelId } from '../../projectProducts/wazzupUtils';
import { useTooltip } from './useTooltip';
import PositionedModal from './PositionedModal';

// Маппинг сокращенных названий на полные
const REGION_SHORT_TO_FULL = {
  'СПБ': 'Санкт-Петербург',
  'МСК': 'Москва',
  'КРД': 'Краснодар',
};

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  'бордюр',
  'бордюр дорожный',
  'брусчатка',
  'брусчатка и тротуарная плитка',
  'плитка тротуарная',
  'газонная решетка',
  'лоток водоотводный жби',
  'лоток водоотводный тротуарный',
]);

const isGazDeliveryCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('газобетон') || GAZ_DELIVERY_CATEGORY_NAMES.has(normalized);
};

// Функция получения короткого названия региона
const getShortRegionName = (regionName) => {
  if (!regionName) return 'СПБ';
  const name = regionName.toUpperCase();
  if (name.includes('СПБ') || name.includes('САНКТ') || name.includes('ПЕТЕРБУРГ')) return 'СПБ';
  if (name.includes('МСК') || name.includes('МОСКВ')) return 'МСК';
  if (name.includes('КРД') || name.includes('КРАСНОДАР')) return 'КРД';
  return name;
};

const BITRIX_SERVICE_CANDIDATES = {
  'secondary-manipulator-trip': [
    'Доставка манипулятором с разгрузкой',
    'Манипулятор 10тн',
  ],
  'secondary-manipulator-trip-no-unload': [
    'Доставка манипулятором без разгрузки',
    'Манипулятор 10тн',
  ],
  'secondary-manipulator-service': [
    'Доставка манипулятором с разгрузкой',
    'Услуга по работе манипулятора',
    'Разгрузка манипулятором',
    'Разгрузка (манипулятор 10т)',
  ],
  'secondary-crane-service': [
    'Услуга по работе автокрана',
    'Разгрузка краном',
  ],
};

// Подгоняет высоту textarea превью-сообщения под контент (до 60vh, дальше скролл).
const autoSizeMessageTextarea = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  // Кап оставляет место под шапку и подвал модалки, чтобы скроллилось
  // только поле (а не поле + контейнер) и кнопки оставались видимыми.
  const maxH = Math.max(160, window.innerHeight - 380);
  el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
};

// Общая пустая ссылка: без неё пропс ручных цен менялся бы каждый рендер.
const EMPTY_PRICE_OVERRIDES = {};

const Offer = forwardRef(({ selectedOffer, addressAndCoordinates, dealId, clientName, onModalOpen, onModalClose, data, deliveryMethod, setDeliveryMethod, setSelectedOffer, paymentMethod, isMskGazobetonScope = false, deliveryDate, products, gazCheapestHint, gazBrandHint, onSelectGazProduction, gazOrderVariantMode = null, setGazOrderVariantMode }, ref) => {
  const { options } = useOptions();
  const { analogsData } = useAnalogsData();
  const localRef = useRef(null);
  const tableContainerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Старые состояния (для совместимости)
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWazzupModal, setShowWazzupModal] = useState(false);
  const [showWazzupResultModal, setShowWazzupResultModal] = useState(false);
  const [wazzupResult, setWazzupResult] = useState({ type: '', message: '' });

  // Состояния для модального окна сообщения
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [currentOfferForMessage, setCurrentOfferForMessage] = useState(null);

  // Состояния для Telegram
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showTelegramResultModal, setShowTelegramResultModal] = useState(false);
  const [telegramResult, setTelegramResult] = useState({ type: '', message: '' });
  const [telegramProfileId, setTelegramProfileId] = useState(null);
  // Состояния для MAX
  const [showMaxModal, setShowMaxModal] = useState(false);
  const [showMaxResultModal, setShowMaxResultModal] = useState(false);
  const [maxResult, setMaxResult] = useState({ type: '', message: '' });

  const [formData, setFormData] = useState({
    regionId: '',
    materialId: '',
    tagId: '',
    competitorPrice: '',
    comment: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Новые состояния для LogistRequestModal (как в ProductionsTable)
  const [showLogistRequestModal, setShowLogistRequestModal] = useState(false);
  const [logistFormData, setLogistFormData] = useState({
    regionId: '',
    regionName: '',
    comment: '',
    items: []
  });
  const [logistFormErrors, setLogistFormErrors] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoadingLogistOptions, setIsLoadingLogistOptions] = useState(false);
  const [departmentTree, setDepartmentTree] = useState(null);
  const [flows, setFlows] = useState([]);

  // Старые состояния
  const [materialOptions, setMaterialOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [responsibleName, setResponsibleName] = useState('');
  const [responsiblePhone, setResponsiblePhone] = useState('');
  const [responsibleId, setResponsibleId] = useState(null);
  const [clientPhone, setClientPhone] = useState(null);
  const [currentOfferToSend, setCurrentOfferToSend] = useState(null);
  const [excludedCategories, setExcludedCategories] = useState([]);
  const [paymentType, setPaymentType] = useState('Нал'); // Форма оплаты по воронке
  // Внутри МСК Газобетона начальное значение берётся из выбора, пережившего пересчёт:
  // иначе после смены способа разгрузки сводка показывала бы выбранный вариант, а в
  // сделку и в сообщение клиенту уходил бы вариант по умолчанию. Вне области значение
  // прежнее.
  const [commercialProposalSendMode, setCommercialProposalSendMode] = useState(
    isMskGazobetonScope && gazOrderVariantMode ? gazOrderVariantMode : 'partial'
  );
  // Ручные цены КП живут здесь, а не в таблице: их читает и экран, и сборка товаров
  // сделки. Пока состояние было ниже, менеджер называл клиенту согласованную цену,
  // а в Битрикс уходила цена расчёта — и рядом с кнопками об этом не было сказано.
  //
  // Хранятся по предложению: у исходного расчёта и у каждого аналога свои строки и
  // свои цены, общий словарь смешал бы их между собой. Второго состояния при этом
  // не появляется — таблица только читает и пишет сюда.
  const [gazPriceOverridesByOffer, setGazPriceOverridesByOffer] = useState({});
  const gazPriceOverridesOf = (type) => gazPriceOverridesByOffer[type] || EMPTY_PRICE_OVERRIDES;
  // Сборка товаров сделки идёт только по исходному расчёту: аналоги заполняются
  // общим путём и своих строк КП в Битрикс не отдают.
  const gazPriceOverrides = gazPriceOverridesByOffer.original || EMPTY_PRICE_OVERRIDES;
  const handleGazPriceOverridesChange = useCallback((type, next) => {
    setGazPriceOverridesByOffer((prev) => ({ ...prev, [type]: next || {} }));
  }, []);

  React.useImperativeHandle(ref, () => localRef.current);

  const formatPriceForMessage = (number) => {
    const rounded = Math.ceil(Number(number));
    const formattedInteger = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return formattedInteger;
  };

  const formatQuantityForMessage = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return value;
    }
    return number % 1 === 0 ? String(number) : number.toFixed(3).replace(/\.?0+$/, '');
  };

  const getMessageModalTarget = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) {
      return null;
    }

    const secondaryBlock = container.querySelector('[data-gaz-block-key="secondary"]');
    if (secondaryBlock) {
      return secondaryBlock;
    }

    return container;
  }, []);

  const {
    activeOffers,
    localPrices,
    localDeliveryPrice,
    localSkidka,
    localSkidkaD,
    localGlobalSkidka,
    per,
    analogs,
    analogDeliveries,
    buttonStates,
    notification,
    tooltip,
    responseText,
    setTooltip,
    setNotification,
    handleMaterialPriceChange,
    handleSkidkaChange,
    handleDeliveryPriceChange,
    handleDeliveryUpdate,
    handleDeliveryMethodChange,
    handleSkidkaChangeDelivery,
    handleGlobalSkidkaChange,
    skidkaOrMarzha,
    handleButtonClick,
    handleFillBitrix,
    addMaterialFromTooltip,
    addMaterialsFromTooltip,
    updateAdditionalMaterialQuantity,
    removeMaterial,
    buildUnderfilledShalandaReduction,
    applyUnderfilledShalandaReduction,
  } = useOfferState(selectedOffer, addressAndCoordinates, dealId, ref, options, clientName, analogsData, deliveryMethod, setDeliveryMethod, setSelectedOffer);

  // Признак области передаётся явно: без него билдер берёт значение по умолчанию
  // (false), разгрузка варианта не подставляется, и в отправку уходят нули там, где
  // на экране КП стоит посчитанная сумма. Тот же вызов в gazProposalSendOptions
  // признак передаёт — расходиться эти два пути не должны.
  const prepareOfferForSend = useCallback((offerToSend) => {
    return buildGazCommercialProposalSendPayload(offerToSend, commercialProposalSendMode, { isMskGazobetonScope });
  }, [commercialProposalSendMode, isMskGazobetonScope]);

  const withPreparedOffer = useCallback((handler) => (offerToSend) => {
    const preparedOffer = prepareOfferForSend(offerToSend);

    if (!preparedOffer?.materials?.length) {
      setNotification({
        type: 'error',
        message: commercialProposalSendMode === 'partial'
          ? 'В неполном КП нет данных для отправки.'
          : 'Нет данных для отправки.',
      });
      return;
    }

    handler(preparedOffer);
  }, [commercialProposalSendMode, prepareOfferForSend, setNotification]);

  const resolveBitrixIdByNomenclature = useCallback(async (nomenclatures) => {
    const candidates = Array.isArray(nomenclatures) ? nomenclatures : [nomenclatures];

    for (const candidate of candidates) {
      if (!candidate) continue;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/get-nomenclature-from-bitrix?nomenclature=${encodeURIComponent(candidate)}`,
          { credentials: 'include' }
        );
        const result = await response.json();

        if (response.ok && result?.success && result?.data?.bitrixId) {
          return result.data.bitrixId;
        }
      } catch (error) {
        console.error(`[Bitrix] Не удалось найти номенклатуру "${candidate}":`, error);
      }
    }

    return '';
  }, []);

  // Для газобетона: строит товарные строки Bitrix из блоков КП, а не из плоского offer.materials.
  // Порядок строк в Bitrix соответствует блочной структуре КП.
  const handleFillBitrixWrapped = useCallback(async (offerToSend) => {
    const isGaz = Array.isArray(offerToSend?.materials)
      && offerToSend.materials.some((material) => {
        const calculator = String(material?.calculatorCategory || material?.calculator || '').toLowerCase();
        const category = String(material?.category || '').toLowerCase();
        return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category);
      });

    if (!isGaz) {
      const prepared = prepareOfferForSend(offerToSend);
      if (!prepared?.materials?.length) {
        setNotification({ type: 'error', message: 'Нет данных для отправки.' });
        return;
      }
      handleFillBitrix(prepared);
      return;
    }

    // Признак области передаётся явно: без него сборщик идёт базовым путём и сам
    // пересчитывает число машин по одной норме погрузки, хотя в заказе несколько
    // толщин и у каждой своя. Строки отсюда уходят в товары сделки количеством
    // рейсов, то есть на экране пять машин, а в Битриксе шесть.
    const proposal = buildGazCommercialProposalBlocks(offerToSend, { isMskGazobetonScope });
    if (!proposal.isGazobetonOffer) {
      handleFillBitrix(offerToSend);
      return;
    }

    const blocksToInclude = commercialProposalSendMode === 'partial'
      ? proposal.blocks.filter((block) => block.key === 'main')
      : proposal.blocks.filter((block) => block.rows.length > 0);

    const isOriginal = offerToSend.type === 'original';
    const deliveryBitrixId = offerToSend.delivery?.bitrixId || '';
    const deliveryMeasureCode = offerToSend.delivery?.measureCode || '796';
    const sourceMaterials = offerToSend.materials || [];
    const resolvedServiceBitrixIds = new Map();
    const unresolvedServiceRows = [];

    const products = [];
    const mergeBitrixProducts = (items) => {
      const grouped = new Map();

      items.forEach((item) => {
        const key = [
          item.bitrixId,
          item.price,
          item.measureCode,
        ].join('__');

        const existing = grouped.get(key);
        if (existing) {
          existing.quantity = Number(existing.quantity || 0) + Number(item.quantity || 0);
          return;
        }

        grouped.set(key, {
          ...item,
          quantity: Number(item.quantity || 0),
        });
      });

      return Array.from(grouped.values());
    };
    // Ручные цены применяются только внутри области: за её пределами их вводить
    // негде, а ветка сборки газобетонных строк общая и с Питером.
    const manualPriceOf = (row) => (isMskGazobetonScope
      ? resolveGazRowManualPrice(row, {
        unloadMethod: offerToSend?.delivery?.deliveryBreakdown?.unloadMethod,
        overrides: gazPriceOverrides,
      })
      : null);

    for (const block of blocksToInclude) {
      for (const row of block.rows) {
        if (row.kind === 'material' && Number.isInteger(row.originalIndex)) {
          const sourceMaterial = sourceMaterials[row.originalIndex] || {};
          const manualPrice = manualPriceOf(row);
          products.push({
            rowKind: 'material',
            bitrixId: sourceMaterial.bitrixId || row.name,
            // Ручная цена — самое позднее и самое осознанное решение менеджера:
            // именно её он назвал клиенту. Поэтому она идёт впереди правки в
            // колонке и впереди цены расчёта.
            price: manualPrice !== null
              ? Math.ceil(manualPrice)
              : (isOriginal
                ? parseFloat(localPrices[row.originalIndex]) || Math.ceil(row.pricePerOne)
                : Math.ceil(row.pricePerOne)),
            quantity: parseFloat(row.quantity) || 1,
            measureCode: sourceMaterial.measureCode || '796',
          });
        } else if (row.kind === 'service') {
          const isDeliveryTrip = row.key === 'main-shalanda'
            || row.key === 'secondary-shalanda'
            || row.key === 'secondary-manipulator-trip'
            || row.key === 'secondary-manipulator-trip-no-unload';
          const canReuseOfferDeliveryBitrixId = isDeliveryTrip
            && String(offerToSend.delivery?.name || '').trim() === String(row.name || '').trim();
          let bitrixId = canReuseOfferDeliveryBitrixId ? deliveryBitrixId : '';

          if (!bitrixId) {
            if (!resolvedServiceBitrixIds.has(row.key)) {
              const resolvedBitrixId = await resolveBitrixIdByNomenclature([
                row.name,
                ...(BITRIX_SERVICE_CANDIDATES[row.key] || []),
              ]);
              resolvedServiceBitrixIds.set(row.key, resolvedBitrixId || '');
            }
            bitrixId = resolvedServiceBitrixIds.get(row.key) || '';
          }

          if (!bitrixId) {
            unresolvedServiceRows.push(row.name);
            continue;
          }

          const manualServicePrice = manualPriceOf(row);
          products.push({
            rowKind: 'service',
            bitrixId,
            price: Math.ceil(manualServicePrice !== null ? manualServicePrice : row.pricePerOne),
            quantity: parseFloat(row.quantity) || 1,
            measureCode: deliveryMeasureCode,
          });
        }
      }
    }

    if (unresolvedServiceRows.length > 0) {
      setNotification({
        type: 'error',
        message: `Не удалось найти в Bitrix номенклатуры для строк: ${[...new Set(unresolvedServiceRows)].join(', ')}`,
      });
      return;
    }

    if (!products.length) {
      setNotification({
        type: 'error',
        message: commercialProposalSendMode === 'partial'
          ? 'В неполном КП нет данных для отправки.'
          : 'Нет данных для отправки.',
      });
      return;
    }

    const materialProducts = products.filter((product) => product.rowKind === 'material');
    const serviceProducts = products.filter((product) => product.rowKind !== 'material');
    const mergedProducts = [
      ...mergeBitrixProducts(materialProducts).map(({ rowKind, ...product }) => product),
      ...mergeBitrixProducts(serviceProducts).map(({ rowKind, ...product }) => product),
    ];

    handleFillBitrix(offerToSend, mergedProducts);
  }, [
    commercialProposalSendMode,
    prepareOfferForSend,
    handleFillBitrix,
    isMskGazobetonScope,
    gazPriceOverrides,
    localPrices,
    resolveBitrixIdByNomenclature,
    setNotification,
  ]);

  const buildFlatOfferMessageBody = useCallback((offerToSend, dealId) => {
    const totalSum = offerToSend.materials.reduce((sum, mat) => sum + (parseInt(mat.sum, 10) || 0), 0) +
      (offerToSend.delivery && offerToSend.delivery.name !== 'Самовывоз' ? parseInt(offerToSend.delivery.totalCost, 10) || 0 : 0);
    const deliveryText = offerToSend.delivery && offerToSend.delivery.name !== 'Самовывоз'
      ? `\n${offerToSend.delivery.name} ${offerToSend.delivery.numberOfTrips} шт по ${offerToSend.delivery.costPerTrip} руб. (${formatPriceForMessage(offerToSend.delivery.totalCost)} руб.)`
      : '';

    // Шапка: внутри области строка с номером заказа убирается целиком, если номера нет.
    const intro = buildGazMessageIntro({ dealId, isMskGazobetonScope });

    // - Условия второго сорта дописываются и здесь, а не только в газобетонном КП
    //   (13.10.2026, Роман). Этим путём сообщение собирается, когда заказ вышел за
    //   область МСК-газобетона — например к блокам добавили товар другой категории.
    //   Невозвратность и 100% оплата не должны зависеть от того, каким путём ушло
    //   сообщение. Пусто, если второго сорта в заказе нет.
    const vtoroySortLines = buildVtoroySortMessageLines(offerToSend.materials);
    const hvost = vtoroySortLines.length > 0 ? `\n\n${vtoroySortLines.join('\n')}` : '';

    return `${intro}\n\n${offerToSend.materials.map((mat) => `${mat.name} ${mat.quantity} ${mat.unit} по ${formatPriceForMessage(mat.pricePerOne)} руб. (${formatPriceForMessage(mat.sum)} руб.)`).join('\n')}${deliveryText}\n\nОбщая сумма: ${formatPriceForMessage(totalSum)} руб.${hvost}`;
  }, [isMskGazobetonScope]);

  const buildGazOfferMessageBody = useCallback((offerToSend, dealId) => {
    // Тот же признак и по той же причине, что в сборке товаров сделки: число
    // рейсов в перечислении позиций должно совпадать с посчитанным, а не быть
    // выведенным заново.
    const proposal = buildGazCommercialProposalBlocks(offerToSend, { isMskGazobetonScope });
    if (!proposal.isGazobetonOffer) {
      return buildFlatOfferMessageBody(offerToSend, dealId);
    }

    const allRows = proposal.blocks.flatMap((block) => block.rows);
    const totalSum = proposal.blocks.reduce((sum, block) => sum + (Number(block.total) || 0), 0);

    const isGazMessageRow = (row) => {
      const name = String(row?.name || '').toLowerCase();
      return name.includes('газобетон');
    };

    const materialRows = allRows.filter((row) => row.kind === 'material');
    const gazMaterialRows = materialRows.filter(isGazMessageRow);
    const extraMaterialRows = materialRows.filter((row) => !isGazMessageRow(row));
    const shalandaRows = allRows.filter((row) => row.key === 'main-shalanda' || row.key === 'secondary-shalanda');
    const manipulatorTripRows = allRows.filter((row) => row.key === 'secondary-manipulator-trip' || row.key === 'secondary-manipulator-trip-no-unload');
    const unloadingRows = allRows.filter((row) => row.key === 'secondary-manipulator-service' || row.key === 'secondary-crane-service');

    const mergeRows = (rows, getMergeMeta) => {
      const grouped = new Map();

      rows.forEach((row) => {
        const mergeMeta = getMergeMeta ? getMergeMeta(row) : {
          key: `${row.name}__${row.unit}__${Number(row.pricePerOne) || 0}`,
          name: row.name,
        };
        const key = mergeMeta.key;
        const existing = grouped.get(key);

        if (existing) {
          existing.quantity = Number(existing.quantity) + Number(row.quantity || 0);
          existing.sum = Number(existing.sum) + Number(row.sum || 0);
          return;
        }

        grouped.set(key, {
          ...row,
          name: mergeMeta.name,
          quantity: Number(row.quantity || 0),
          sum: Number(row.sum || 0),
        });
      });

      return Array.from(grouped.values());
    };

    const formatRow = (row) =>
      `${row.name} ${formatQuantityForMessage(row.quantity)} ${row.unit} по ${formatPriceForMessage(row.pricePerOne)} руб. (${formatPriceForMessage(row.sum)} руб.)`;

    const sections = [];

    if (gazMaterialRows.length > 0 || extraMaterialRows.length > 0) {
      const formattedMaterialRows = [];

      if (gazMaterialRows.length > 0) {
        formattedMaterialRows.push(...mergeRows(gazMaterialRows).map(formatRow));
      }

      if (extraMaterialRows.length > 0) {
        formattedMaterialRows.push(...mergeRows(extraMaterialRows).map(formatRow));
      }

      sections.push(formattedMaterialRows.join('\n'));
    }
    if (shalandaRows.length > 0) {
      sections.push(mergeRows(shalandaRows).map(formatRow).join('\n'));
    }
    if (manipulatorTripRows.length > 0) {
      sections.push(mergeRows(manipulatorTripRows).map(formatRow).join('\n'));
    }
    if (unloadingRows.length > 0) {
      sections.push(mergeRows(unloadingRows).map(formatRow).join('\n'));
    }

    // Шапка и итог — единственные строки, которые правит область. Перечисление
    // позиций выше собрано по строкам блоков: там уже стоит точная номенклатура
    // выбранного производства и доставка выбранного варианта КП. Названий блоков
    // («Основной блок», «Остаток») в тексте нет и не было.
    const intro = buildGazMessageIntro({ dealId, isMskGazobetonScope });
    const totalLines = buildGazMessageTotalLines({
      total: totalSum,
      paymentMethod,
      isMskGazobetonScope,
      formatPrice: formatPriceForMessage,
    });

    // - Условия второго сорта дописываются после итога: невозвратный блок и 100%
    //   оплата — то, что клиент должен прочитать своими глазами, а не услышать
    //   только по телефону (12.10.2026, Роман). Пусто, если второго сорта в заказе нет.
    const vtoroySortLines = buildVtoroySortMessageLines(gazMaterialRows);
    const hvost = vtoroySortLines.length > 0 ? `\n\n${vtoroySortLines.join('\n')}` : '';

    return `${intro}\n\n${sections.join('\n\n')}\n\n${totalLines.join('\n')}${hvost}`;
  }, [buildFlatOfferMessageBody, isMskGazobetonScope, paymentMethod]);

  // offerToSend уже подготовлен через withPreparedOffer → prepareOfferForSend,
  // повторный вызов prepareOfferForSend ломает partial-режим (фантомный второй блок)
  const buildOfferMessageBody = useCallback((offerToSend, dealId) => {
    const hasGazMaterial = Array.isArray(offerToSend?.materials)
      && offerToSend.materials.some((material) => {
        const calculator = String(material?.calculatorCategory || material?.calculator || '').toLowerCase();
        const category = String(material?.category || '').toLowerCase();
        const name = String(material?.name || '').toLowerCase();
        return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category) || name.includes('газобетон');
      });

    return hasGazMaterial ? buildGazOfferMessageBody(offerToSend, dealId) : buildFlatOfferMessageBody(offerToSend, dealId);
  }, [buildFlatOfferMessageBody, buildGazOfferMessageBody]);

  // Инициализация useTooltip на верхнем уровне
  const { handleMouseEnter, handleMouseLeave } = useTooltip(setTooltip, tableContainerRef, options, formatPriceForMessage);


  useEffect(() => {
    const fetchResponsibleFromDeal = async () => {
      if (!dealId) {
        console.log('⚠️ fetchResponsibleFromDeal: dealId не передан');
        return;
      }

      console.log(`🔍 fetchResponsibleFromDeal: запрашиваем сделку ${dealId}`);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
          { credentials: "include" }
        );
        const result = await response.json();

        console.log(`📥 Ответ get-deal: success=${result.success}, data=`, result.data);

        if (result.success) {
          const assignedById = result.data.ASSIGNED_BY_ID;
          const categoryId = parseInt(result.data.CATEGORY_ID, 10);

          console.log(`📊 CATEGORY_ID из сделки: ${result.data.CATEGORY_ID} -> parseInt: ${categoryId}`);

          // Определяем форму оплаты по воронке
          const categoryToPaymentType = {
            0: 'Нал',      // Частные лица
            1: 'Безнал',   // Юридические лица
            2: 'Нал',      // Повторные продажи ЧЛ
            3: 'Безнал'    // Повторные продажи ЮЛ
          };
          const determinedPaymentType = categoryToPaymentType[categoryId] || 'Нал';
          setPaymentType(determinedPaymentType);
          console.log(`💳 Форма оплаты по воронке CATEGORY_ID=${categoryId}: ${determinedPaymentType}`);

          const userResponse = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${assignedById}`,
            { credentials: "include" }
          );
          const userResult = await userResponse.json();
          if (userResult.success) {
            const responsible = `${userResult.data.NAME || ''} ${userResult.data.LAST_NAME || ''}`.trim();
            const workPhone = userResult.data.WORK_PHONE ? userResult.data.WORK_PHONE.replace(/\D/g, '') : '';
            setResponsibleName(responsible);
            setResponsiblePhone(workPhone);
            setResponsibleId(assignedById);
            console.log("Ответственный:", responsible, "Телефон:", workPhone);
          } else {
            setNotification({ type: 'error', message: 'Ошибка при получении данных ответственного.' });
          }
        } else {
          console.error('❌ get-deal вернул success=false:', result);
          setNotification({ type: 'error', message: 'Ошибка при получении данных сделки.' });
        }
      } catch (err) {
        console.error("❌ Ошибка при получении данных менеджера:", err);
        setNotification({ type: 'error', message: 'Ошибка при получении данных менеджера.' });
      }
    };

    fetchResponsibleFromDeal();
  }, [dealId, setNotification]);

  // Функция загрузки потоков (как в ProductionsTable)
  const loadFlows = useCallback(async () => {
    if (flows && flows.length > 0) {
      console.log('✅ Потоки уже загружены');
      return;
    }
    try {
      console.log('📥 Загружаем потоки...');
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/bitrix/flows`,
        { credentials: "include" }
      );
      const result = await response.json();
      if (result.success) {
        setFlows(result.flows || []);
        console.log(`✅ Потоки загружены:`, result.flows);
      } else {
        console.error('❌ Ошибка загрузки потоков:', result.error);
        setFlows([]);
      }
    } catch (err) {
      console.error('❌ Ошибка при загрузке потоков:', err);
      setFlows([]);
    }
  }, [flows]);

  // Функция загрузки department-tree (как в ProductionsTable)
  const loadDepartmentTree = useCallback(async () => {
    if (departmentTree) {
      console.log('✅ Department-tree уже загружен');
      return;
    }
    try {
      console.log('📥 Загружаем department-tree...');
      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/department-tree/15`, {
        credentials: "include",
      });
      const result = await response.json();
      if (result.success) {
        setDepartmentTree(result.structure);
        localStorage.setItem('departmentTree_15', JSON.stringify(result.structure));
        localStorage.setItem('departmentTree_15_timestamp', Date.now().toString());
        console.log('✅ Department-tree загружен:', result.structure);
      } else {
        console.error('❌ Ошибка загрузки department-tree:', result.error);
      }
    } catch (err) {
      console.error('❌ Ошибка при загрузке department-tree:', err);
    }
  }, [departmentTree]);

  // Bitrix API URL
  const BITRIX_WEBHOOK_URL = process.env.REACT_APP_ROOF_BITRIX_CRM_DEAL_PRODUCTSROWS_SET;

  // Функция загрузки данных для dropdown'ов (из Bitrix API как в ServicePage)
  const fetchLogistDropdownOptions = useCallback(async () => {
    console.log('📥 Загружаем опции для dropdown\'ов логиста из Bitrix...');

    // Проверяем актуальные значения из state
    if (regionOptions.length > 0 && tagOptions.length > 0) {
      console.log('[fetchLogistDropdownOptions] Опции уже загружены, пропускаем');
      return true;
    }

    try {
      const response = await fetch(`${BITRIX_WEBHOOK_URL}/mlt.logistics.tags.list`);
      const data = await response.json();
      console.log('[fetchLogistDropdownOptions] Ответ от Bitrix:', data);

      if (data && data.result) {
        const { regions, tags } = data.result;

        if (regions) {
          setRegionOptions(regions.map(item => ({
            value: item.ID,
            label: item.NAME,
          })));
          console.log(`✅ Регионы загружены (${regions.length}):`, regions);
        }

        if (tags) {
          setTagOptions(tags.map(item => ({
            value: item.ID,
            label: item.NAME,
          })));
          console.log(`✅ Теги загружены (${tags.length}):`, tags);
        }

        return true;
      } else {
        throw new Error('Не удалось загрузить данные из Bitrix API');
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки опций из Bitrix:', error);
      // Fallback значения
      setRegionOptions([
        { value: "1", label: "СПБ" },
        { value: "2", label: "МСК" },
        { value: "3", label: "КРД" },
      ]);
      setTagOptions([
        { value: "1", label: "Общестрой" },
        { value: "2", label: "Кровля" },
        { value: "3", label: "Утеплитель" },
      ]);
      return false;
    }
  }, [BITRIX_WEBHOOK_URL, regionOptions.length, tagOptions.length]);

  // Функция предзагрузки данных (как в ProductionsTable)
  const preloadLogistData = useCallback(async () => {
    console.log('🔄 preloadLogistData вызван');

    try {
      setIsLoadingLogistOptions(true);

      // Загружаем регионы и теги
      await fetchLogistDropdownOptions();

      // Загружаем потоки
      if (!flows || flows.length === 0) {
        try {
          console.log('📥 Загружаем потоки...');
          const response = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/bitrix/flows`,
            { credentials: "include" }
          );
          const result = await response.json();
          if (result.success) {
            setFlows(result.flows || []);
            console.log(`✅ Потоки загружены:`, result.flows);
          } else {
            console.error('❌ Ошибка загрузки потоков:', result.error);
            setFlows([]);
          }
        } catch (err) {
          console.error('❌ Ошибка при загрузке потоков:', err);
          setFlows([]);
        }
      }

      // Загружаем department-tree
      if (!departmentTree) {
        try {
          console.log('📥 Загружаем department-tree...');
          const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/department-tree/15`, {
            credentials: "include",
          });
          const result = await response.json();
          if (result.success) {
            setDepartmentTree(result.structure);
            localStorage.setItem('departmentTree_15', JSON.stringify(result.structure));
            localStorage.setItem('departmentTree_15_timestamp', Date.now().toString());
            console.log('✅ Department-tree загружен:', result.structure);
          } else {
            console.error('❌ Ошибка загрузки department-tree:', result.error);
          }
        } catch (err) {
          console.error('❌ Ошибка при загрузке department-tree:', err);
        }
      }
    } catch (error) {
      console.error('Ошибка при предзагрузке данных:', error);
    } finally {
      setIsLoadingLogistOptions(false);
    }
  }, [BITRIX_WEBHOOK_URL]);

  useEffect(() => {
    // Загружаем данные для новой логики логиста
    try {
      preloadLogistData();
    } catch (error) {
      console.error('❌ Ошибка при предзагрузке данных логиста:', error);
    }

    // Загружаем данные ответственного (старая логика)
    const fetchResponsibleFromDeal = async () => {
      if (!dealId) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
          { credentials: "include" }
        );
        const result = await response.json();
        if (result.success) {
          const assignedById = result.data.ASSIGNED_BY_ID;
          const userResponse = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${assignedById}`,
            { credentials: "include" }
          );
          const userResult = await userResponse.json();
          if (userResult.success) {
            const responsible = `${userResult.data.NAME || ''} ${userResult.data.LAST_NAME || ''}`.trim();
            const workPhone = userResult.data.WORK_PHONE ? userResult.data.WORK_PHONE.replace(/\D/g, '') : '';
            setResponsibleName(responsible);
            setResponsiblePhone(workPhone);
            setResponsibleId(assignedById);
            console.log("Ответственный:", responsible, "Телефон:", workPhone);
          }
        }
      } catch (err) {
        console.error("Ошибка при получении данных менеджера:", err);
      }
    };

    fetchResponsibleFromDeal();
  }, [dealId]);

  // ================================================
  // ФУНКЦИИ ИЗ PRODUCTIONS TABLE
  // ================================================

  // Функция конвертации названия региона в внутренний ID для API category-mappings
  const getRegionIdByName = (regionName) => {
    if (!regionName) return '1'; // СПБ по умолчанию
    const name = regionName.toUpperCase();
    if (name.includes('СПБ') || name.includes('САНКТ') || name.includes('ПЕТЕРБУРГ')) return '1';
    if (name.includes('МСК') || name.includes('МОСКВ')) return '2';
    if (name.includes('КРД') || name.includes('КРАСНОДАР')) return '3';
    return '1';
  };

  // Функция получения материала и тега из БД
  const getMaterialAndTagByCategory = async (categoryName, regionName) => {
    if (!categoryName) return null;
    try {
      const regionId = getRegionIdByName(regionName);
      console.log(`[getMaterialAndTagByCategory] Ищем материал и тег для категории "${categoryName}" в регионе "${regionName}" (ID: ${regionId})`);
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/category-mappings?category=${encodeURIComponent(categoryName)}&regionId=${regionId}`
      );
      const result = await response.json();
      if (result && result.success && result.data) {
        console.log(`[getMaterialAndTagByCategory] ✅ Найдено:`, result.data);
        return result.data;
      } else {
        console.log(`[getMaterialAndTagByCategory] ❌ Ничего не найдено для категории "${categoryName}"`);
        return null;
      }
    } catch (error) {
      console.error('[getMaterialAndTagByCategory] Ошибка:', error);
      return null;
    }
  };

  // Поиск поддепартамента по материалу (вспомогательная функция)
  function findSubDepartmentByMaterial(dept, terms) {
    for (const term of terms) {
      if (dept.NAME && dept.NAME.toLowerCase().includes(term)) {
        return dept;
      }
    }
    if (dept.subDepartments && dept.subDepartments.length > 0) {
      for (const subDept of dept.subDepartments) {
        const found = findSubDepartmentByMaterial(subDept, terms);
        if (found) return found;
      }
    }
    return null;
  }

  // Поиск поддепартамента по материалу во всех департаментах дерева
  const findSubDepartmentInAllDepartments = (deptArr, searchTerms) => {
    for (const dept of deptArr) {
      if (dept.subDepartments && dept.subDepartments.length > 0) {
        for (const subDept of dept.subDepartments) {
          const found = findSubDepartmentByMaterial(subDept, searchTerms);
          if (found) return found;
        }
      }
    }
    return null;
  };

  // Поиск сотрудника в department-tree по региону и материалу/тегу (fallback)
  const findEmployeeInDepartmentTree = (departmentTree, regionName, materialName, tagName) => {
    if (!departmentTree) {
      console.log('[findEmployee] Нет данных department-tree');
      return null;
    }

    // Проверяем структуру department-tree
    const structure = Array.isArray(departmentTree)
      ? departmentTree
      : (departmentTree?.structure || [departmentTree]);
    if (structure.length === 0) {
      console.log('[findEmployee] Department-tree пуст');
      return null;
    }

    const regionToDepartment = {
      'Санкт-Петербург': ['Департамент ЛОГ ОС', 'Департамент ЛОГ НЦ', 'ЛОГ ОС', 'ЛОГ НЦ', 'ОС'],
      'СПБ': ['Департамент ЛОГ ОС', 'Департамент ЛОГ НЦ', 'ЛОГ ОС', 'ЛОГ НЦ', 'ОС'],
      'Москва': ['ЛОГ Московский филиал ОС', 'ЛОГ Московский филиал НЦ', 'МСК'],
      'МСК': ['ЛОГ Московский филиал ОС', 'ЛОГ Московский филиал НЦ', 'МСК'],
      'Краснодар': ['Департамент ЛОГ Краснодар', 'ЛОГ Краснодар', 'КРД'],
      'КРД': ['Департамент ЛОГ Краснодар', 'ЛОГ Краснодар', 'КРД'],
    };

    const materialToDepartment = {
      'утеплитель': ['Теплоизоляция', 'Утеплитель'],
      'теплоизоляция': ['Теплоизоляция', 'Утеплитель'],
      'кровля': ['Кровля, профлист', 'кровля'],
      'газобетон': ['Газобетон', 'газобетон'],
      'кирпич': ['Кирпич', 'кирпич'],
      'бетон': ['Бетон', 'бетон'],
      'общестрой': ['Общестрой', 'Общестрой'],
      'нерудка': ['Нерудка', 'Нерудка 2', 'мск_Нерудка', 'нерудка'],
      'металл': ['Металл', 'металл'],
    };

    const searchRegionAliases = regionToDepartment[regionName] || [regionName];

    const findDepartmentByRegion = (dept, regionAliases) => {
      for (const alias of regionAliases) {
        if (dept.NAME && dept.NAME.toLowerCase().includes(alias.toLowerCase())) {
          return dept;
        }
      }
      if (dept.subDepartments && dept.subDepartments.length > 0) {
        for (const subDept of dept.subDepartments) {
          const found = findDepartmentByRegion(subDept, regionAliases);
          if (found) return found;
        }
      }
      return null;
    };

    let regionDepartment = null;
    for (const dept of structure) {
      regionDepartment = findDepartmentByRegion(dept, searchRegionAliases);
      if (regionDepartment) break;
    }

    if (!regionDepartment) {
      regionDepartment = structure[0];
      if (!regionDepartment) return null;
    }

    const searchTerms = [materialName.toLowerCase()];
    if (tagName) {
      searchTerms.push(tagName.toLowerCase());
      const synonyms = materialToDepartment[tagName.toLowerCase()];
      if (synonyms) searchTerms.push(...synonyms.map(s => s.toLowerCase()));
    }

    const materialDepartment = findSubDepartmentByMaterial(regionDepartment, searchTerms);

    if (!materialDepartment) {
      const globalMaterialDepartment = findSubDepartmentInAllDepartments(structure, searchTerms);
      if (globalMaterialDepartment && globalMaterialDepartment.employees && globalMaterialDepartment.employees.length > 0) {
        return globalMaterialDepartment.employees[0];
      }
      if (regionDepartment.employees && regionDepartment.employees.length > 0) {
        return regionDepartment.employees[0];
      }
      return null;
    }

    if (materialDepartment.employees && materialDepartment.employees.length > 0) {
      return materialDepartment.employees[0];
    }

    if (regionDepartment.employees && regionDepartment.employees.length > 0) {
      return regionDepartment.employees[0];
    }

    return null;
  };

  // Функция получения категорий из Bitrix
  const fetchCategoriesFromBitrix = useCallback(async (nomenclatures) => {
    if (nomenclatures.length === 0) return {};
    const uniqueNomenclatures = [...new Set(nomenclatures)];
    try {
      console.log(`[fetchCategoriesFromBitrix] Запрос категорий из Bitrix для ${uniqueNomenclatures.length} товаров`);
      const categories = {};
      const categoryPromises = uniqueNomenclatures.map(async (nomenclature) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/get-nomenclature-from-bitrix?nomenclature=${encodeURIComponent(nomenclature)}`,
            { credentials: "include" }
          );
          const result = await response.json();
          if (result.success && result.data && result.data.category) {
            categories[nomenclature] = result.data.category;
            console.log(`[fetchCategoriesFromBitrix] ✅ Найдена категория для "${nomenclature}": ${result.data.category}`);
          } else {
            categories[nomenclature] = '';
            console.log(`[fetchCategoriesFromBitrix] ⚠️ Категория не найдена для "${nomenclature}"`);
          }
        } catch (err) {
          console.error(`[fetchCategoriesFromBitrix] ❌ Ошибка для "${nomenclature}":`, err);
          categories[nomenclature] = '';
        }
      });
      await Promise.all(categoryPromises);
      console.log(`[fetchCategoriesFromBitrix] Итоговый результат:`, categories);
      return categories;
    } catch (err) {
      console.error('[fetchCategoriesFromBitrix] Ошибка при получении категорий из Bitrix:', err);
      return {};
    }
  }, []);

  // Функция определения тега по категории (вспомогательная)
  const getTagByCategory = (category) => {
    if (!category) return 'Общестрой';
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('бетон')) return 'Бетон';
    if (categoryLower.includes('газобетон')) return 'Газобетон';
    if (categoryLower.includes('кирпич')) return 'Кирпич';
    if (categoryLower.includes('кровля') || categoryLower.includes('профлист')) return 'Кровля';
    if (categoryLower.includes('металл')) return 'Металл';
    if (categoryLower.includes('щебень') || categoryLower.includes('песок') || categoryLower.includes('нерудка')) return 'Нерудка';
    return 'Общестрой';
  };

  // Функция для определения нужного flowId на основе материала
  const findFlowIdByMaterial = (item, flowsArr) => {
    if (!flowsArr || flowsArr.length === 0) return 25; // Fallback

    const materialName = item?.materialFromDB || item?.material || item?.tag || '';
    const materialNameLower = materialName.toLowerCase();

    let flow = flowsArr.find(f =>
      f.name.toLowerCase() === materialNameLower
    );

    if (!flow) {
      flow = flowsArr.find(f => {
        const flowNameLower = f.name.toLowerCase();
        return materialNameLower.includes(flowNameLower) || flowNameLower.includes(materialNameLower);
      });
    }

    if (!flow) {
      const keywordMap = [
        { keywords: ['бетон', 'жби'], flowName: 'бетон' },
        { keywords: ['газобетон'], flowName: 'газобетон' },
        { keywords: ['кирпич', 'стеновые'], flowName: 'кирпич' },
        { keywords: ['кровля', 'профлист', 'фасад', 'забор'], flowName: 'кровля' },
        { keywords: ['металл'], flowName: 'металл' },
        { keywords: ['теплоизоляция', 'утеплитель'], flowName: 'теплоизоляция' },
        { keywords: ['нерудка', 'щебень', 'песок'], flowName: 'нерудка' },
      ];

      for (const { keywords, flowName } of keywordMap) {
        if (keywords.some(keyword => materialNameLower.includes(keyword))) {
          flow = flowsArr.find(f => f.name.toLowerCase().includes(flowName));
          if (flow) break;
        }
      }
    }

    if (flow) {
      return flow.id;
    } else {
      return 25;
    }
  };

  // Функция закрытия модального окна логиста
  const handleCloseLogistRequestModal = () => {
    setShowLogistRequestModal(false);
  };

  // Функция изменения полей в форме
  const handleItemChange = (itemId, field, value) => {
    setLogistFormData(prev => {
      const items = prev.items.map(item => {
        if (item.id === itemId) {
          return { ...item, [field]: value };
        }
        return item;
      });
      return { ...prev, items };
    });
  };

  // Функция валидации формы (как в ProductionsTable)
  const validateLogistForm = () => {
    const errors = {};
    if (!logistFormData.regionId) {
      errors.region = "Поле 'Регион' обязательно";
    }

    // Проверяем каждый блок товара
    logistFormData.items.forEach((item) => {
      if (!item.material) {
        errors[`${item.id}_material`] = "Поле 'Материал' обязательно";
      }
      if (!item.quantity) {
        errors[`${item.id}_quantity`] = "Поле 'Количество' обязательно";
      }
      if (!item.price) {
        errors[`${item.id}_price`] = "Поле 'Цена' обязательно";
      }
      if (!item.competitorPrice) {
        errors[`${item.id}_competitorPrice`] = "Поле обязательно";
      }
    });

    setLogistFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Функция открытия модального окна логиста (адаптированная для Offer)
  const handleOpenLogistRequestModal = useCallback(async () => {
    try {
      console.log('🚀 Открываем модальное окно логиста для Offer', { dealId });

      // Загружаем paymentType из сделки
      if (dealId) {
        try {
          const dealResponse = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
            { credentials: "include" }
          );
          const dealResult = await dealResponse.json();
          if (dealResult.success && dealResult.data) {
            const categoryId = parseInt(dealResult.data.CATEGORY_ID, 10);
            const categoryToPaymentType = {
              0: 'Нал',      // Частные лица
              1: 'Безнал',   // Юридические лица
              2: 'Нал',      // Повторные продажи ЧЛ
              3: 'Безнал'    // Повторные продажи ЮЛ
            };
            const determinedPaymentType = categoryToPaymentType[categoryId] || 'Нал';
            setPaymentType(determinedPaymentType);
            console.log(`💳 Форма оплаты по воронке CATEGORY_ID=${categoryId}: ${determinedPaymentType}`);
          }
        } catch (e) {
          console.error('Ошибка получения paymentType:', e);
        }
      }

      // Загружаем данные для dropdown'ов
      await preloadLogistData();

      // Получаем материалы из selectedOffer
      const materialsToShow = selectedOffer?.materials || [];
      console.log(`[handleOpenLogistRequestModal] Материалов в offer: ${materialsToShow.length}`, materialsToShow);

      if (materialsToShow.length === 0) {
        console.log('Нет материалов для отправки');
        return;
      }

      // Определяем регион - используем регион из logistFormData или выбранный пользователем в dropdown
      // selectedRegion из пропсов НЕ меняется при выборе в dropdown!
      const shortRegionName = getShortRegionName(logistFormData.regionName) || 'СПБ';
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || 'Санкт-Петербург';

      // Находим соответствующий regionOption
      const regionOption = regionOptions.find(opt =>
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      const regionValue = regionOption?.value || '';

      console.log(`[handleOpenLogistRequestModal] Определенный регион: ${shortRegionName} (value: ${regionValue})`);

      // Получаем категории из Bitrix
      const uniqueNomenclatures = [...new Set(materialsToShow.map(m => m.name).filter(n => n))];
      console.log(`[handleOpenLogistRequestModal] Уникальных номенклатур: ${uniqueNomenclatures.length}`, uniqueNomenclatures);

      const categoriesFromBitrix = await fetchCategoriesFromBitrix(uniqueNomenclatures);
      console.log(`[handleOpenLogistRequestModal] Категории из Bitrix:`, categoriesFromBitrix);

      // Подготавливаем данные для формы
      const itemsPromises = materialsToShow.map(async (material) => {
        const nomenclature = material.name || '';
        const categoryFromBitrix = categoriesFromBitrix[nomenclature] || material.category || material.calculator || '';

        // Получаем материал и тег из БД (основной источник)
        const materialTagInfo = await getMaterialAndTagByCategory(categoryFromBitrix, shortRegionName);

        // Используем тег из БД, если есть, иначе fallback на getTagByCategory
        const tagFromDB = materialTagInfo?.tag || getTagByCategory(categoryFromBitrix);
        const materialFromDB = materialTagInfo?.material || '';

        // Определяем сотрудника через department-tree
        let employee = null;
        if (departmentTree) {
          employee = findEmployeeInDepartmentTree(
            departmentTree,
            shortRegionName,
            materialFromDB || nomenclature,
            tagFromDB
          );
        }

        console.log(`[handleOpenLogistRequestModal] Товар "${nomenclature}": category=${categoryFromBitrix}, tag=${tagFromDB}, material=${materialFromDB}, employee=`, employee);

        return {
          id: 0,
          material: nomenclature,
          nomenclature: nomenclature,
          category: categoryFromBitrix,
          tagId: tagFromDB,
          materialFromDB: materialFromDB,
          materialId: materialTagInfo?.materialId || '',
          materialBitrixId: materialTagInfo?.materialBitrixId || '',
          tagIdFromDB: materialTagInfo?.tag || '',
          employee: employee,
          quantity: material.quantity || 1,
          unit: material.unit || material.measure || 'шт',
          measureCode: material.measureCode || '796',
          price: material.pricePerOne || '',
          competitorPrice: '',
          comment: ``
        };
      });

      const items = await Promise.all(itemsPromises);
      let idCounter = 1;
      items.forEach(item => {
        item.id = idCounter++;
      });

      setLogistFormData({
        offerId: selectedOffer?.id || '',
        regionId: regionValue,
        regionName: fullRegionName,
        comment: '',
        items: items
      });

      setShowLogistRequestModal(true);
    } catch (error) {
      console.error('Ошибка при открытии модального окна логиста:', error);
    }
  }, [selectedOffer, regionOptions, fetchCategoriesFromBitrix, departmentTree, getMaterialAndTagByCategory, findEmployeeInDepartmentTree, dealId, setPaymentType]);

  // ================================================================
  // Устанавливаем регион после загрузки regionOptions
  // ================================================================
  useEffect(() => {
    if (regionOptions.length > 0 && showLogistRequestModal && !logistFormData.regionId) {
      const shortRegionName = 'СПБ'; // По умолчанию СПБ
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || 'Санкт-Петербург';

      const regionOption = regionOptions.find(opt =>
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      if (regionOption) {
        setLogistFormData(prev => ({
          ...prev,
          regionId: regionOption.value,
          regionName: regionOption.label
        }));
        console.log('[useEffect] Регион установлен:', regionOption.label);
      }
    }
  }, [regionOptions, showLogistRequestModal, logistFormData.regionId]);

  // ================================================================
  // Пересчитываем items при изменении региона в dropdown
  // ================================================================
  useEffect(() => {
    if (!showLogistRequestModal || !logistFormData.regionId || logistFormData.items?.length > 0) return;

    const recalculateItems = async () => {
      console.log('[useEffect] Пересчитываем items для региона:', logistFormData.regionName);

      const shortRegionName = getShortRegionName(logistFormData.regionName) || 'СПБ';
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || logistFormData.regionName || 'Санкт-Петербург';

      // Находим regionOption для текущего региона
      const regionOption = regionOptions.find(opt =>
        opt.value === logistFormData.regionId ||
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      const regionValue = regionOption?.value || logistFormData.regionId;

      try {
        // Получаем materials из selectedOffer
        const materialsToShow = selectedOffer?.materials || [];
        const uniqueNomenclatures = [...new Set(materialsToShow.map(m => m.name).filter(n => n))];
        const categoriesFromBitrix = await fetchCategoriesFromBitrix(uniqueNomenclatures);

        // Формируем items с правильным regionId
        const itemsPromises = materialsToShow.map(async (material) => {
          const nomenclature = material.name || '';
          const categoryFromBitrix = categoriesFromBitrix[nomenclature] || material.category || material.calculator || '';
          const materialTagInfo = await getMaterialAndTagByCategory(categoryFromBitrix, shortRegionName);
          const tagFromDB = materialTagInfo?.tag || getTagByCategory(categoryFromBitrix);
          const materialFromDB = materialTagInfo?.material || '';

          return {
            id: 0,
            material: nomenclature,
            nomenclature: nomenclature,
            category: categoryFromBitrix,
            tagId: tagFromDB,
            materialFromDB: materialFromDB,
            materialId: materialTagInfo?.materialId || '',
            materialBitrixId: materialTagInfo?.materialBitrixId || '',
            tagIdFromDB: materialTagInfo?.tag || '',
            employee: null,
            quantity: material.quantity || 1,
            unit: material.unit || material.measure || 'шт',
            measureCode: material.measureCode || '796',
            price: material.pricePerOne || '',
            competitorPrice: '',
            comment: ``
          };
        });

        const items = await Promise.all(itemsPromises);
        let idCounter = 1;
        items.forEach(item => { item.id = idCounter++; });

        console.log('[useEffect] items пересчитаны:', items);

        setLogistFormData(prev => ({
          ...prev,
          regionId: regionValue,
          regionName: fullRegionName,
          items: items
        }));
      } catch (error) {
        console.error('[useEffect] Ошибка при пересчёте items:', error);
      }
    };

    recalculateItems();
  }, [showLogistRequestModal, logistFormData.regionId, logistFormData.regionName, selectedOffer, regionOptions, fetchCategoriesFromBitrix]);

  // ================================================

  const isDataAvailable = selectedOffer && selectedOffer.materials && (selectedOffer.delivery || selectedOffer.deliveryIncludedInMaterials);
  const hasBeneficialAnalogs = analogs.slice(1).some(analog => analog && analog.volumeBenefit < 0);
  const hasAnalogs = analogs.length > 1;
  // Проверяем наличие валидных аналогов
  const hasValidAnalogs = hasAnalogs && analogs.slice(1).some((analog, index) => {
    const analogKey = `analog${index + 1}`;
    const delivery = analogDeliveries[analogKey];
    return analog && analog.volumeBenefit < 0 && (
      selectedOffer.delivery?.name === 'Самовывоз' ||
      (delivery && delivery.totalCost > 0)
    );
  });

  // Условие рендеринга кнопок
  const shouldRenderButtons = isDataAvailable &&
    selectedOffer.materials.length === 1 &&
    hasValidAnalogs &&
    (selectedOffer.materials[0].name.toLowerCase().includes("утеплитель") ||
      selectedOffer.materials[0].name.toLowerCase().includes("экструдированный пенополистирол") ||
      (options[selectedOffer.materials[0].name]?.CATEGORY || '').toLowerCase().includes("osb"));

  const getLabelById = (options, id) => {
    const option = options.find(opt => opt.value === id);
    return option ? option.label : '';
  };

  const handleOpenRequestModal = () => {
    setShowRequestModal(true);
    if (onModalOpen) onModalOpen();
  };

  useEffect(() => {
    console.log(activeOffers.original)
  }, [activeOffers.original])

  useEffect(() => {
    if (activeOffers.original?.materials) {
      const excludedCategories = activeOffers.original.materials.map((material) => material.category)
      setExcludedCategories(excludedCategories)
    } else {
      const excludedCategories = selectedOffer.materials.map((material) => material.category)
      setExcludedCategories(excludedCategories)
    }

    console.log(excludedCategories)
  }, [selectedOffer.materials, selectedOffer.production, activeOffers.original?.materials])

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setFormData({
      regionId: '',
      materialId: '',
      tagId: '',
      competitorPrice: '',
      comment: '',
    });
    setFormErrors({});
    if (onModalClose) onModalClose();
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors(prev => ({
      ...prev,
      [field.replace('Id', '')]: '',
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.regionId) errors.region = "Поле 'Регион' обязательно";
    if (!formData.materialId) errors.material = "Поле 'Материал' обязательно";
    if (!formData.tagId) errors.tag = "Поле 'Тег' обязательно";
    if (!formData.competitorPrice) errors.competitorPrice = "Поле 'Цена конкурентов' обязательно";
    return errors;
  };

  const handleSubmitRequest = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      const payload = {
        regionId: formData.regionId,
        materialId: formData.materialId,
        tagId: formData.tagId,
        competitorPrice: formData.competitorPrice,
        comment: formData.comment,
        dealId: dealId,
        clientName: clientName,
      };

      console.log("Отправка запроса логисту в Bitrix:", payload);
      const BITRIX_SUBMIT_URL = `${BITRIX_WEBHOOK_URL}mlt.logistics.tasks.set`;

      const requestData = {
        DEAL_ID: parseInt(payload.dealId),
        TASKS: JSON.stringify([
          {
            tag: payload.tagId,
            price: payload.competitorPrice,
            comment: payload.comment,
          },
        ]),
      };

      try {
        const response = await axios.post(BITRIX_SUBMIT_URL, requestData, {
          headers: { 'Content-Type': 'application/json' },
        });
        console.log('Успешный ответ:', response.data);
      } catch (error) {
        console.error('Ошибка при отправке запроса:', error.response?.data || error.message);
      }

      setShowConfirmModal(false);
      setShowRequestModal(false);
      setNotification({ type: 'success', message: 'Запрос успешно отправлен логисту!' });
      setFormData({
        regionId: '',
        materialId: '',
        tagId: '',
        competitorPrice: '',
        comment: '',
      });
      if (onModalClose) onModalClose();
    } catch (error) {
      console.error("Ошибка при отправке запроса:", error);
      setShowConfirmModal(false);
      setShowRequestModal(false);
      setNotification({ type: 'error', message: 'Не удалось отправить запрос логисту.' });
      if (onModalClose) onModalClose();
    }
  };

  const sendToWazzup = (offerToSend) => {
    if (!offerToSend || !offerToSend.materials || offerToSend.materials.length === 0) {
      setNotification({ type: 'error', message: 'Нет данных для отправки в Wazzup.' });
      return;
    }
    setCurrentOfferToSend(offerToSend);
    setShowWazzupModal(true);
  };

  const handleWazzupConfirm = async () => {
    setShowWazzupModal(false);
    setIsLoadingOptions(true);

    try {
      let phone = clientPhone;
      if (!phone) {
        phone = await fetchClientContact(dealId);
        if (!phone) {
          throw new Error("Номер телефона клиента не найден");
        }
        setClientPhone(phone);
      }

      const channelId = await getWazzupChannelId(responsibleName);
      if (!channelId) {
        throw new Error(`Канал Wazzup не найден для менеджера ${responsibleName}`);
      }

      const offerToSend = currentOfferToSend;
      if (!offerToSend) {
        throw new Error("Нет данных для отправки в Wazzup.");
      }

      const messageBody = buildOfferMessageBody(offerToSend, dealId);
      const messageText = `${messageBody}\n\n_Ваш персональный менеджер_\n*${responsibleName}*\n${responsiblePhone || ''}`;
      const referenceId = uuidv4();

      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/send-wazzup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channelId,
          chatType: "whatsapp",
          chatId: phone,
          text: messageText,
          referenceId,
          meta: {},
        }),
        credentials: "include",
      });

      const result = await response.json();
      if (response.status === 200 && result.success) {
        console.log("Сообщение успешно отправлено в Wazzup:", result);
        setWazzupResult({ type: 'success', message: 'Товары успешно отправлены в Wazzup!' });
      } else {
        throw new Error(result.message || `Ошибка ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка при отправке в Wazzup:", err);
      setWazzupResult({ type: 'error', message: `Ошибка: ${err.message}` });
    } finally {
      setIsLoadingOptions(false);
      setShowWazzupResultModal(true);
    }
  };

  const handleCloseWazzupResultModal = () => {
    setShowWazzupResultModal(false);
    setWazzupResult({ type: '', message: '' });
    setCurrentOfferToSend(null);
  };

  // Функция для формирования и отображения сообщения
  const createMessage = (offerToSend) => {
    if (!offerToSend || !offerToSend.materials || offerToSend.materials.length === 0) {
      setNotification({ type: 'error', message: 'Нет данных для формирования сообщения.' });
      return;
    }
    setCurrentOfferForMessage(offerToSend);

    const text = `${buildOfferMessageBody(offerToSend, dealId)}\n\nВаш персональный менеджер\n${responsibleName}\n${responsiblePhone || ''}`;

    setMessageText(text);
    setShowMessageModal(true);
  };

  // Внутри МСК Газобетона модального окна сообщения больше нет: готовое КП показано
  // прямо на странице (см. GazClientProposal), там же правка текста и копирование.
  // Вне области путь остаётся базовым.
  const handleCreateMessageEntry = (rawOffer) => {
    if (isMskGazobetonScope) {
      return;
    }
    withPreparedOffer(createMessage)(rawOffer);
  };

  // Функция для копирования текста в буфер обмена
  const copyMessageToClipboard = async () => {
    try {
      // Создаем временный textarea для копирования (fallback для битрикса)
      const textArea = document.createElement('textarea');
      textArea.value = messageText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus({ preventScroll: true });
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        const { toast } = await import('react-toastify');
        toast.success('Сообщение скопировано в буфер обмена!', { autoClose: 3000 });
      } else {
        throw new Error('Copy command failed');
      }
    } catch (err) {
      // Пробуем новый API как запасной вариант
      try {
        await navigator.clipboard.writeText(messageText);
        const { toast } = await import('react-toastify');
        toast.success('Сообщение скопировано в буфер обмена!', { autoClose: 3000 });
      } catch (clipboardErr) {
        const { toast } = await import('react-toastify');
        toast.error('Не удалось скопировать сообщение. Разрешите доступ к буферу обмена в браузере.', { autoClose: 5000 });
      }
    }
  };

  const sendToMax = async (offerToSend) => {
    if (!offerToSend || !offerToSend.materials || offerToSend.materials.length === 0) {
      setNotification({ type: 'error', message: 'Нет данных для отправки в MAX.' });
      return;
    }

    setCurrentOfferToSend(offerToSend);
    setShowMaxModal(true);
  };


  // Функции для работы с Telegram
  const sendToTelegram = async (offerToSend) => {
    if (!offerToSend || !offerToSend.materials || offerToSend.materials.length === 0) {
      setNotification({ type: 'error', message: 'Нет данных для отправки в Telegram.' });
      return;
    }

    try {
      // Получаем данные пользователя для получения Profile ID
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${responsibleId}`,
        { credentials: "include" }
      );
      const result = await response.json();

      if (result.success && result.data && result.data.UF_WEB_SITES) {
        const profileId = result.data.UF_WEB_SITES;
        setTelegramProfileId(profileId);
        window.telegramProfileId = profileId;
        setCurrentOfferToSend(offerToSend);
        setShowTelegramModal(true);
      } else {
        setNotification({
          type: 'error',
          message: 'Для вашего отдела пока данная функция недоступна'
        });
      }
    } catch (error) {
      console.error('Ошибка при получении  :', error);
      setNotification({ type: 'error', message: 'Ошибка при получении Profile ID' });
    }
  };

  const handleTelegramConfirm = async () => {
    setShowTelegramModal(false);
    setIsLoadingOptions(true);

    try {
      let phone = clientPhone;
      if (!phone) {
        phone = await fetchClientContact(dealId);
        if (!phone) {
          throw new Error("Номер телефона клиента не найден");
        }
        setClientPhone(phone);
      }

      const profileId = telegramProfileId;
      if (!profileId) {
        throw new Error("Profile ID не найден. Попробуйте снова.");
      }

      const offerToSend = currentOfferToSend;
      if (!offerToSend) {
        throw new Error("Нет данных для отправки в Telegram.");
      }

      const messageText = `${buildOfferMessageBody(offerToSend, dealId)}\n\n<i>Ваш персональный менеджер</i>\n<b>${responsibleName}</b>\n${responsiblePhone || ''}`;

      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/send-telegram?profile_id=${profileId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: "include",
        body: JSON.stringify({
          phone: phone,
          body: messageText,
          meta: {
            dealId: dealId
          }
        })
      });

      const result = await response.json();
      if (response.status === 200 && result.success) {
        console.log("Сообщение успешно отправлено в Telegram:", result);
        setTelegramResult({ type: 'success', message: 'Товары успешно отправлены в Telegram!' });
      } else {
        throw new Error(result.message || `Ошибка ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка при отправке в Telegram:", err);
      setTelegramResult({ type: 'error', message: `Ошибка: ${err.message}` });
    } finally {
      setIsLoadingOptions(false);
      setShowTelegramResultModal(true);
    }
  };

  const handleMaxConfirm = async () => {
    setShowMaxModal(false);
    setIsLoadingOptions(true);

    try {
      let phone = clientPhone;
      if (!phone) {
        phone = await fetchClientContact(dealId);
        if (!phone) {
          throw new Error("Номер телефона клиента не найден");
        }
        setClientPhone(phone);
      }

      if (!responsibleId) throw new Error("Не удалось определить ответственного");

      const userResponse = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${responsibleId}`,
        { credentials: "include" }
      );
      const userResult = await userResponse.json();
      const profileId = userResult.success ? userResult.data.UF_TWITTER : null;
      if (!profileId) throw new Error("Для вашего аккаунта MAX не настроен");

      const checkResponse = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/check-max-registration?phone=${encodeURIComponent(phone)}&profileId=${encodeURIComponent(profileId)}`,
        { credentials: "include" }
      );
      const checkResult = await checkResponse.json();
      if (!checkResponse.ok || !checkResult.success) {
        throw new Error(checkResult.message || `Ошибка проверки MAX: ${checkResponse.status}`);
      }
      if (!checkResult.hasMax) {
        throw new Error('Клиент не найден в MAX, либо номер не зарегистрирован');
      }

      const offerToSend = currentOfferToSend;
      if (!offerToSend) {
        throw new Error("Нет данных для отправки в MAX.");
      }

      const messageText = `${buildOfferMessageBody(offerToSend, dealId)}\n\n<i>Ваш персональный менеджер</i>\n<b>${responsibleName}</b>\n${responsiblePhone || ''}`;

      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/send-max`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: "include",
        body: JSON.stringify({
          phone: phone,
          body: messageText,
          profileId,
          meta: {
            dealId: dealId
          }
        })
      });

      const result = await response.json();
      if (response.status === 200 && result.success) {
        console.log("Сообщение успешно отправлено в MAX:", result);
        setMaxResult({ type: 'success', message: 'Товары успешно отправлены в MAX!' });
      } else {
        throw new Error(result.message || `Ошибка ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка при отправке в MAX:", err);
      setMaxResult({ type: 'error', message: `Ошибка: ${err.message}` });
    } finally {
      setIsLoadingOptions(false);
      setShowMaxResultModal(true);
    }
  };

  const handleMaxConfirmClipboard = async () => {
    setShowMessageModal(false);
    setIsLoadingOptions(true);

    try {
      let phone = clientPhone;
      if (!phone) {
        phone = await fetchClientContact(dealId);
        if (!phone) throw new Error("Номер телефона клиента не найден");
        setClientPhone(phone);
      }

      if (!responsibleId) throw new Error("Не удалось определить ответственного");

      const userResponse = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${responsibleId}`,
        { credentials: "include" }
      );
      const userResult = await userResponse.json();
      const profileId = userResult.success ? userResult.data.UF_TWITTER : null;
      if (!profileId) throw new Error("Для вашего аккаунта MAX не настроен");

      const checkResponse = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/check-max-registration?phone=${encodeURIComponent(phone)}&profileId=${encodeURIComponent(profileId)}`,
        { credentials: "include" }
      );
      const checkResult = await checkResponse.json();
      if (!checkResponse.ok || !checkResult.success) {
        throw new Error(checkResult.message || `Ошибка проверки MAX: ${checkResponse.status}`);
      }
      if (!checkResult.hasMax) throw new Error('Клиент не найден в MAX, либо номер не зарегистрирован');

      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/send-max`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: "include",
        body: JSON.stringify({ phone, body: messageText, profileId, meta: { dealId } })
      });

      const result = await response.json();
      if (response.status === 200 && result.success) {
        setMaxResult({ type: 'success', message: 'Товары успешно отправлены в MAX!' });
      } else {
        throw new Error(result.message || `Ошибка ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка при отправке в MAX:", err);
      setMaxResult({ type: 'error', message: `Ошибка: ${err.message}` });
    } finally {
      setIsLoadingOptions(false);
      setShowMaxResultModal(true);
    }
  };

  const handleCloseTelegramModal = () => {
    setShowTelegramModal(false);
    setShowTelegramResultModal(false);
    setTelegramResult({ type: '', message: '' });
    setTelegramProfileId(null);
    window.telegramProfileId = null;
    setCurrentOfferToSend(null);
  };

  const handleCloseMaxModal = () => {
    setShowMaxModal(false);
    setShowMaxResultModal(false);
    setMaxResult({ type: '', message: '' });
    setCurrentOfferToSend(null);
  };

  const fetchClientContact = async (dealId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
        { credentials: "include" }
      );
      const result = await response.json();
      if (result.success) {
        const contactId = result.data.CONTACT_ID;
        if (!contactId) {
          console.warn(`Контакт не привязан к сделке ${dealId}`);
          return null;
        }
        const contactResponse = await fetch(
          `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-contact?contactId=${contactId}`,
          { credentials: "include" }
        );
        const contactResult = await contactResponse.json();
        if (contactResult.success) {
          const phone = contactResult.data.PHONE?.[0]?.VALUE;
          return phone ? phone.replace(/\D/g, '').startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}` : null;
        }
        throw new Error("Не удалось получить данные контакта");
      }
      throw new Error("Не удалось получить данные сделки");
    } catch (err) {
      console.error("Ошибка при получении контакта клиента:", err);
      return null;
    }
  };

  return isDataAvailable ? (
    <>
      <style jsx global>{`
        .custom-logist-modal .modal-dialog { max-width: 600px; }
        .custom-logist-modal .modal-content { border-radius: 15px; padding: 20px; background-color: #fff; border: none; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); }
        .custom-logist-modal .modal-title { font-size: 24px; font-weight: bold; color: #333; margin-bottom: 20px; }
        .custom-logist-modal .form-label { font-size: 16px; font-weight: 500; color: #555; margin-bottom: 5px; }
        .custom-logist-modal .form-group { margin-bottom: 20px; }
        .custom-logist-modal .narrow-container { background-color: #f8f9fa; padding: 15px; border-radius: 10px; margin: 20px 0; }
        .custom-logist-modal .textarea-field { width: 100%; min-height: 80px; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; resize: vertical; }
        .custom-logist-modal .error-text { color: #dc3545; font-size: 12px; margin-top: 5px; }
        .custom-logist-modal .modal-footer { border-top: none; justify-content: center; gap: 10px; }
        .custom-logist-modal .btn-cancel { background-color: #6c757d; color: #fff; border: none; border-radius: 25px; padding: 10px 30px; font-size: 16px; font-weight: 500; cursor: pointer; transition: background-color 0.3s ease; }
        .custom-logist-modal .btn-cancel:hover { background-color: #5a6268; }
        .custom-logist-modal .btn-submit { background-color: #007bff; color: #fff; border: none; border-radius: 25px; padding: 10px 30px; font-size: 16px; font-weight: 500; cursor: pointer; transition: background-color 0.3s ease; }
        .custom-logist-modal .btn-submit:hover { background-color: #0056b3; }
        .custom-confirm-modal .modal-dialog { max-width: 400px; }
        .custom-confirm-modal .modal-content { border-radius: 15px; padding: 20px; background-color: #fff; border: none; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); text-align: center; }
        .custom-confirm-modal .modal-body { font-size: 18px; color: #333; margin-bottom: 20px; }
        .custom-wazzup-modal .modal-dialog { max-width: 400px; }
        .custom-wazzup-modal .modal-content { border-radius: 15px; padding: 20px; background-color: #fff; border: none; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); text-align: center; }
        .custom-wazzup-modal .modal-body { font-size: 18px; color: #333; margin-bottom: 20px; }
        .custom-wazzup-result-modal .modal-dialog { max-width: 400px; }
        .custom-wazzup-result-modal .modal-content { border-radius: 15px; padding: 20px; background-color: #fff; border: none; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); text-align: center; }
        .custom-wazzup-result-modal .modal-header { border-bottom: none; }
        .custom-wazzup-result-modal .modal-body { font-size: 18px; color: #333; margin-bottom: 20px; }
        .custom-wazzup-result-modal .modal-footer { border-top: none; }
        .custom-message-modal .modal-dialog { max-width: min(960px, calc(100vw - 48px)); width: min(960px, calc(100vw - 48px)); min-width: 0; }
        .custom-message-modal .modal-content { max-height: min(85vh, 900px); }
        .custom-message-modal textarea.form-control {
          min-height: 420px;
          font-size: 15px;
          line-height: 1.55;
        }
        .btn-max { background-color: #6f42c1; border-color: #6f42c1; color: #fff; }
        .btn-max:hover { background-color: #5b35a0; border-color: #5b35a0; color: #fff; }
        .btn-max-purple { background-color: #6f42c1; border-color: #6f42c1; color: #fff; }
        .btn-max-purple:hover { background-color: #5b35a0; border-color: #5b35a0; color: #fff; }
      `}</style>
      <section className="rd-section rd-kp-section" ref={localRef}>
        <div className="rd-sec-head">
          {/* Внутри области заголовок называет задачу экрана и не повторяет первую
              строку самого КП. Вне области — как в 5442898. */}
          <h5>{isMskGazobetonScope ? 'Расчёт заказа' : 'Коммерческое предложение'}</h5>
        </div>

        {(() => {
          const isGazobetonOffer = activeOffers.original
            && buildGazCommercialProposalBlocks(activeOffers.original, { isMskGazobetonScope }).isGazobetonOffer;
          if (isGazobetonOffer) return null;
          const uniqueSources = [...new Set(selectedOffer.materials.map(m => m.source).filter(Boolean))];
          return (
            <div className="rd-kp-head">
              <div className="rd-kp-head__prod">
                {/* Выбранное производство */}
                <b>{selectedOffer.production}</b>
                {/* {uniqueSources.length === 1 && `Источник: ${uniqueSources[0]}`} */}
              </div>
              <div className="rd-kp-head__discount">
                <span
                  onClick={skidkaOrMarzha}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  title="Переключить: скидка / маржа"
                >
                  Общая {per.text.toLowerCase()}
                </span>
                <NumberInput
                  value={localGlobalSkidka || ''}
                  onChange={(value) => handleGlobalSkidkaChange(value, 'original')}
                />
                <span>%</span>
              </div>
            </div>
          );
        })()}

        {activeOffers.original && (
          <>
            <OfferTable
              offer={activeOffers.original}
              offerType="original"
              activeOffers={activeOffers}
              selectedOffer={selectedOffer}
              localPrices={localPrices}
              localDeliveryPrice={localDeliveryPrice}
              localSkidka={localSkidka}
              localSkidkaD={localSkidkaD}
              localGlobalSkidka={localGlobalSkidka}
              per={per}
              tableContainerRef={tableContainerRef}
              onMaterialPriceChange={handleMaterialPriceChange}
              onSkidkaChange={handleSkidkaChange}
              onDeliveryPriceChange={handleDeliveryPriceChange}
              onDeliveryUpdate={handleDeliveryUpdate}
              onDeliveryMethodChange={handleDeliveryMethodChange}
              onSkidkaChangeDelivery={handleSkidkaChangeDelivery}
              onGlobalSkidkaChange={handleGlobalSkidkaChange}
              onSkidkaOrMarzha={skidkaOrMarzha}
              commercialProposalSendMode={commercialProposalSendMode}
              onCommercialProposalSendModeChange={setCommercialProposalSendMode}
              gazOrderVariantMode={gazOrderVariantMode}
              onGazOrderVariantModeChange={setGazOrderVariantMode}
              gazPriceOverrides={gazPriceOverridesOf('original')}
              onGazPriceOverridesChange={handleGazPriceOverridesChange}
              onFillBitrix={handleFillBitrixWrapped}
              onSendToWazzup={withPreparedOffer(sendToWazzup)}
              onSendToTelegram={withPreparedOffer(sendToTelegram)}
              onSendToMax={withPreparedOffer(sendToMax)}
              onCreateMessage={handleCreateMessageEntry}
              setTooltip={setTooltip}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              options={options}
              data={data}
              selectedProduction={selectedOffer.production}
              orderProducts={products}
              gazCheapestHint={gazCheapestHint}
              gazBrandHint={gazBrandHint}
              onSelectGazProduction={onSelectGazProduction}
              deliveryMethods={selectedOffer.deliveryMethods}
              onAddMaterial={addMaterialFromTooltip}
              onAddMaterials={addMaterialsFromTooltip}
              onAdditionalMaterialQuantityChange={updateAdditionalMaterialQuantity}
              onRemoveMaterial={removeMaterial}
              onBuildUnderfilledShalandaReduction={buildUnderfilledShalandaReduction}
              onApplyUnderfilledShalandaReduction={applyUnderfilledShalandaReduction}
              excludedCategories={excludedCategories}
              paymentMethod={paymentMethod}
              isMskGazobetonScope={isMskGazobetonScope}
              addressAndCoordinates={addressAndCoordinates}
            />

          </>
        )}

        {/* <ClientInfo selectedOffer={selectedOffer} clientName={clientName} /> */}

        {/* {shouldRenderButtons ? (
          <div className="text-start mt-4">
            <div className="mb-2">
              <button className="rd-scenario-btn rd-scenario-btn--neutral" onClick={() => handleButtonClick(1)}>
                {buttonStates[1] ? 'Скрыть' : '- НЕ БРОНИРУЕМ'}
              </button>
              {buttonStates[1] && responseText && (
                <div className="text-start fs-4 mt-2">{responseText}</div>
              )}
            </div>

            {analogs.slice(1).map((analog, index) => {
              const analogKey = `analog${index + 1}`;
              const buttonIndex = index + 2;
              const delivery = analogDeliveries[analogKey];
              if (!analog || analog.volumeBenefit >= 0 ||
                (selectedOffer.delivery?.name !== 'Самовывоз' && (!delivery || delivery.totalCost <= 0))) {
                return null;
              }
              const costDifference = Math.round(
                (selectedOffer.materials[0].sum + (selectedOffer.delivery?.name !== 'Самовывоз' ? selectedOffer.delivery?.totalCost || 0 : 0)) -
                (analog.pricePerPackage * analog.requiredPackages + (delivery?.totalCost || 0))
              );
              return (
                <div key={analogKey} className="mb-2">
                  <button
                    className="rd-scenario-btn rd-scenario-btn--accent"
                    onClick={() => handleButtonClick(buttonIndex)}
                  >
                    {buttonStates[buttonIndex] ? 'Скрыть' : `Да, есть цена дешевле (общая стоимость меньше на ${Math.round(
                      (selectedOffer.materials[0].sum + (selectedOffer.delivery?.totalCost || 0)) -
                      (analog.pricePerPackage * analog.requiredPackages + (analogDeliveries[analogKey]?.totalCost || 0))
                    ).toLocaleString()} руб.)`}
                  </button>
                  {activeOffers[analogKey] && buttonStates[buttonIndex] && (
                    <div className="mt-2">
                      {index < 2 && ( // Текст выводится только для первых двух аналогов (index 0 и 1)
                        <span className="fs-4">
                          {clientName}, {index === 0 ?
                            `тогда возьмите ${activeOffers[analogKey].materials[0].name}, он дешевле на ` :
                            `есть вариант дешевле на `}
                          <strong>{Math.round(
                            (selectedOffer.materials[0].sum + (selectedOffer.delivery?.totalCost || 0)) -
                            (analog.pricePerPackage * analog.requiredPackages + (analogDeliveries[analogKey]?.totalCost || 0))
                          ).toLocaleString()}</strong> рублей.
                          {index === 0 ?
                            ` На самом деле он гораздо теплее и форматстабильнее, под ваши цели он идеально подходит, так что давайте забронируем пока он есть?` :
                            ` При этом вы получите утеплитель с такими же характеристиками, но другого бренда. Это ${activeOffers[analogKey].materials[0].name}. Итого на Ваш объем это будет `}
                          {index === 0 ? '' : <strong>{(analog.pricePerPackage * analog.requiredPackages).toLocaleString()}</strong>} рублей
                          <br /><strong>БРОНИРУЕМ???</strong>
                        </span>
                      )}
                      {analog.isIzolife && (
                        <div className="mt-2 mb-1">
                          {analog.izolifeComment && (
                            <div style={{ color: 'red', fontWeight: 500 }}>
                              {analog.izolifeComment}
                            </div>
                          )}
                          {analog.izolifePhrase && (
                            <div style={{ color: 'red', fontWeight: 500, marginTop: 4 }}>
                              {analog.izolifePhrase}
                            </div>
                          )}
                        </div>
                      )}
                      <OfferTable
                        offer={activeOffers[analogKey]}
                        offerType={analogKey}
                        activeOffers={activeOffers}
                        selectedOffer={selectedOffer}
                        localPrices={localPrices}
                        localDeliveryPrice={localDeliveryPrice}
                        localSkidka={localSkidka}
                        localSkidkaD={localSkidkaD}
                        localGlobalSkidka={localGlobalSkidka}
                        per={per}
                        tableContainerRef={tableContainerRef}
                        onMaterialPriceChange={handleMaterialPriceChange}
                        onSkidkaChange={handleSkidkaChange}
                        onDeliveryPriceChange={handleDeliveryPriceChange}
                        onDeliveryUpdate={handleDeliveryUpdate}
                        onSkidkaChangeDelivery={handleSkidkaChangeDelivery}
                        onGlobalSkidkaChange={handleGlobalSkidkaChange}
                        onSkidkaOrMarzha={skidkaOrMarzha}
                        commercialProposalSendMode={commercialProposalSendMode}
                        onCommercialProposalSendModeChange={setCommercialProposalSendMode}
                        onFillBitrix={handleFillBitrix}
                        onSendToWazzup={sendToWazzup}
                        onSendToTelegram={sendToTelegram}
                        onSendToMax={sendToMax}
                        setTooltip={setTooltip}
                        handleMouseEnter={handleMouseEnter}
                        handleMouseLeave={handleMouseLeave}
                        options={options}
                        data={data}
                        title={`Аналог ${index + 1}`}
                        onAddMaterial={addMaterialFromTooltip}
                        onAddMaterials={addMaterialsFromTooltip}
                        onAdditionalMaterialQuantityChange={updateAdditionalMaterialQuantity}
                        onRemoveMaterial={removeMaterial}
                        onBuildUnderfilledShalandaReduction={buildUnderfilledShalandaReduction}
                        onApplyUnderfilledShalandaReduction={applyUnderfilledShalandaReduction}
                        excludedCategories={excludedCategories}
                        paymentMethod={paymentMethod}
                        isMskGazobetonScope={isMskGazobetonScope}
                        gazPriceOverrides={gazPriceOverridesOf(analogKey)}
                        onGazPriceOverridesChange={handleGazPriceOverridesChange}
                        addressAndCoordinates={addressAndCoordinates}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="mb-2">
              <button className="rd-scenario-btn rd-scenario-btn--neutral" onClick={() => handleButtonClick(8)}>
                {buttonStates[8] ? 'Скрыть' : 'Нет, предложенный аналог хуже'}
              </button>
              {buttonStates[8] && (
                <div className="text-start mt-2">
                  <span className="fs-4">Подождите, я уточню у логиста и перезвоню вам в течение часа<br /></span>
                  <Button
                    variant="primary"
                    onClick={handleOpenLogistRequestModal}
                    className="fs-4"
                    disabled={isLoadingLogistOptions}
                  >
                    {isLoadingLogistOptions ? 'Загрузка...' : 'Отправить запрос логисту'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : null} */}

        <PositionedModal
          show={!!notification}
          onHide={() => {
            setNotification(null);
            if (onModalClose) onModalClose();
          }}
          targetElement={localRef.current}
        >
          <Modal.Header closeButton>
            <Modal.Title>{notification?.type === 'success' ? 'Успех' : 'Ошибка'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {notification?.message.split('\n').map((line, index) => (
              <span key={index}>
                {line}
                {index < notification.message.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => {
              setNotification(null);
              if (onModalClose) onModalClose();
            }}>ОК</Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showRequestModal}
          onHide={handleCloseRequestModal}
          dialogClassName="custom-logist-modal"
          targetElement={localRef.current}
        >
          <Modal.Header closeButton>
            <Modal.Title>Запрос логисту</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isLoadingOptions ? (
              <div className="text-center">Загрузка данных...</div>
            ) : (
              <Form>
                <Form.Group className="form-group">
                  <Form.Label>Регион</Form.Label>
                  <DropdownTable
                    id="region"
                    options={regionOptions}
                    selectedValue={getLabelById(regionOptions, formData.regionId)}
                    onChange={(value) => handleFormChange('regionId', value)}
                  />
                  {formErrors.region && <div className="error-text">{formErrors.region}</div>}
                </Form.Group>

                <div className="narrow-container">
                  <Form.Group className="form-group">
                    <Form.Label>Материал</Form.Label>
                    <DropdownTable
                      id="material"
                      options={materialOptions}
                      selectedValue={getLabelById(materialOptions, formData.materialId)}
                      onChange={(value) => handleFormChange('materialId', value)}
                    />
                    {formErrors.material && <div className="error-text">{formErrors.material}</div>}
                  </Form.Group>

                  <Form.Group className="form-group">
                    <Form.Label>Тег</Form.Label>
                    <DropdownTable
                      id="tag"
                      options={tagOptions}
                      selectedValue={getLabelById(tagOptions, formData.tagId)}
                      onChange={(value) => handleFormChange('tagId', value)}
                    />
                    {formErrors.tag && <div className="error-text">{formErrors.tag}</div>}
                  </Form.Group>

                  <Form.Group className="form-group">
                    <Form.Label>Цена конкурентов</Form.Label>
                    <textarea
                      className="textarea-field"
                      value={formData.competitorPrice}
                      onChange={(e) => handleFormChange('competitorPrice', e.target.value)}
                    />
                    {formErrors.competitorPrice && <div className="error-text">{formErrors.competitorPrice}</div>}
                  </Form.Group>

                  <Form.Group className="form-group">
                    <Form.Label>Комментарий</Form.Label>
                    <textarea
                      className="textarea-field"
                      value={formData.comment}
                      onChange={(e) => handleFormChange('comment', e.target.value)}
                    />
                  </Form.Group>
                </div>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn-cancel" onClick={handleCloseRequestModal}>
              Отмена
            </Button>
            <Button
              className="btn-submit"
              onClick={handleSubmitRequest}
              disabled={isLoadingOptions}
            >
              Отправить
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showConfirmModal}
          onHide={handleCloseConfirmModal}
          dialogClassName="custom-confirm-modal"
          targetElement={localRef.current}
        >
          <Modal.Body>
            Товары добавятся в сделку. Запрос отправится логисту.
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn-cancel" onClick={handleCloseConfirmModal}>
              Отмена
            </Button>
            <Button className="btn-submit" onClick={handleConfirmSubmit}>
              ОК
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showWazzupModal}
          onHide={() => setShowWazzupModal(false)}
          dialogClassName="custom-wazzup-modal"
          targetElement={localRef.current}
        >
          <Modal.Body>
            Сейчас товары будут отправлены в Wazzup.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWazzupModal(false)}>
              Отмена
            </Button>
            <Button variant="success" onClick={handleWazzupConfirm}>
              Отправить
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showWazzupResultModal}
          onHide={handleCloseWazzupResultModal}
          dialogClassName="custom-wazzup-result-modal"
          targetElement={localRef.current}
        >
          <Modal.Header closeButton>
            <Modal.Title>{wazzupResult.type === 'success' ? 'Успех' : 'Ошибка'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {wazzupResult.message}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className={wazzupResult.type === 'success' ? 'btn-success' : 'btn-danger'}
              onClick={handleCloseWazzupResultModal}
            >
              ОК
            </Button>
          </Modal.Footer>
        </PositionedModal>

        {/* Модальное окно для формирования сообщения */}
        <PositionedModal
          show={showMessageModal}
          onHide={() => setShowMessageModal(false)}
          dialogClassName="custom-message-modal"
          targetElement={getMessageModalTarget()}
        >
          <Modal.Header>
            <Modal.Title>Сформированное сообщение</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="rd-msg-hint">Можно отредактировать перед копированием</div>
            <textarea
              ref={autoSizeMessageTextarea}
              className="rd-msg-textarea"
              rows={12}
              value={messageText}
              onChange={(e) => { setMessageText(e.target.value); autoSizeMessageTextarea(e.target); }}
            />
          </Modal.Body>
          <Modal.Footer className="modal-footer-split">
            <Button className="btn-max btn-max-icon" onClick={handleMaxConfirmClipboard} title="Отправить в MAX" aria-label="Отправить в MAX">
              <img src="https://maxicons.ru/icons/Max_logo_black.svg" alt="MAX" width={24} height={24} />
            </Button>
            <div className="modal-footer-actions">
              <Button variant="secondary" onClick={() => setShowMessageModal(false)}>
                Закрыть
              </Button>
              <Button variant="primary" onClick={copyMessageToClipboard}>
                Копировать в буфер обмена
              </Button>
            </div>
          </Modal.Footer>
        </PositionedModal>

        {/* Новая модалка логиста (как в ProductionsTable) */}
        <LogistRequestModal
          show={showLogistRequestModal}
          onHide={handleCloseLogistRequestModal}
          logistFormData={logistFormData}
          logistFormErrors={logistFormErrors}
          handleItemChange={handleItemChange}
          validateLogistForm={validateLogistForm}
          regionOptions={regionOptions}
          isLoadingLogistOptions={isLoadingLogistOptions}
          setLogistFormData={setLogistFormData}
          dealId={dealId}
          clientName={clientName}
          addressAndCoordinates={addressAndCoordinates}
          flows={flows}
          findFlowIdByMaterial={findFlowIdByMaterial}
          deliveryDate={deliveryDate}
          paymentType={paymentType}
        />

        {/* Модалки для Telegram */}
        <PositionedModal
          show={showTelegramModal}
          onHide={() => setShowTelegramModal(false)}
          dialogClassName="custom-telegram-modal"
          targetElement={localRef.current}
        >
          <Modal.Body>
            Сейчас товары будут отправлены в Telegram.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTelegramModal(false)} style={{ margin: '30px' }}>
              Отмена
            </Button>
            <Button variant="success" onClick={handleTelegramConfirm} style={{ margin: '30px' }}>
              Отправить
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showTelegramResultModal}
          onHide={handleCloseTelegramModal}
          dialogClassName="custom-telegram-result-modal"
          targetElement={localRef.current}
        >
          <Modal.Header closeButton>
            <Modal.Title>{telegramResult.type === 'success' ? 'Успех' : 'Ошибка'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {telegramResult.message}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className={telegramResult.type === 'success' ? 'btn-success' : 'btn-danger'}
              onClick={handleCloseTelegramModal}
            >
              ОК
            </Button>
          </Modal.Footer>
        </PositionedModal>

        {/* Модалки для MAX */}
        <PositionedModal
          show={showMaxModal}
          onHide={() => setShowMaxModal(false)}
          dialogClassName="custom-max-modal"
          targetElement={localRef.current}
        >
          <Modal.Body>
            Сейчас товары будут отправлены в MAX.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMaxModal(false)}>
              Отмена
            </Button>
            <Button className="btn-max" onClick={handleMaxConfirm}>
              Отправить
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <PositionedModal
          show={showMaxResultModal}
          onHide={handleCloseMaxModal}
          dialogClassName="custom-max-result-modal"
          targetElement={localRef.current}
        >
          <Modal.Header closeButton>
            <Modal.Title>{maxResult.type === 'success' ? 'Успех' : 'Ошибка'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {maxResult.message}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className={maxResult.type === 'success' ? 'btn-max' : 'btn-danger'}
              onClick={handleCloseMaxModal}
            >
              ОК
            </Button>
          </Modal.Footer>
        </PositionedModal>

        <Tooltip tooltip={tooltip} tooltipRef={tooltipRef} />
      </section>
    </>
  ) : null;
});

export default Offer;
